/**
 * Network Indicator Component - Visual indicator for network status
 * Following SOLID principles with clean, reusable design
 */

import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { Tooltip, Card, Space, Typography, Button } from "antd";
import { ReloadOutlined, WifiOutlined } from "@ant-design/icons";
import { useNetworkStatus } from "../../contexts/NetworkStatusContext";
import "./NetworkIndicator.scss";

const { Text } = Typography;

/**
 * Network Indicator Component
 * Displays current network status with visual indicators
 */
const NetworkIndicator = ({ 
  position = "top-right",
  showDetails = false,
  showRefreshButton = true,
  className = "",
  size = "medium",
  style = {}
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const networkStatus = useNetworkStatus();
  const {
    isOnline,
    getConnectionQuality,
    getFormattedSpeed,
    // getFormattedLatency,
    checkConnection,
    checkSpeed,
    lastChecked
  } = networkStatus;

  const quality = getConnectionQuality();

  // Update current time every second for real-time "ago" display
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Handle manual refresh
  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      await checkConnection();
      if (isOnline) {
        await checkSpeed();
      }
    } catch (error) {
      console.error("Failed to refresh network status:", error);
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, checkConnection, checkSpeed, isOnline]);

  // Format last checked time - updates in real-time
  const getLastCheckedText = useCallback(() => {
    const diff = currentTime - lastChecked;
    const seconds = Math.floor(diff / 1000);
    
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  }, [currentTime, lastChecked]);

  // Render detailed tooltip content
  const renderTooltipContent = () => (
    <div className="network-indicator-tooltip">
      <Space direction="vertical" size="small">
        <div>
          <Text strong>{quality.label} Connection</Text>
        </div>
        <div>
          <Text type="secondary">{quality.description}</Text>
        </div>
        {/* <div>
          <Text>Speed: {getFormattedSpeed()}</Text>
        </div>
        <div>
          <Text>Latency: {getFormattedLatency()}</Text>
        </div> */}
        <div>
          <Text type="secondary">Updated: {getLastCheckedText()}</Text>
        </div>
        {showRefreshButton && (
          <Button
            type="link"
            size="small"
            icon={<ReloadOutlined spin={isRefreshing} />}
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            Refresh
          </Button>
        )}
      </Space>
    </div>
  );

  // Render simple text with dot indicator
  const renderSimpleIndicator = () => (
    <div 
      className={`network-indicator-simple ${size}`}
      style={style}
      role="status"
      aria-label={`Network status: ${quality.label} - ${quality.description}`}
    >
      <span className="connection-text">Internet Status:</span>
      <div className="status-container">
        <div 
          className="status-dot" 
          style={{ backgroundColor: quality.color }}
        />
        <span className="status-label" style={{ color: quality.color }}>
          {quality.label}
        </span>
      </div>
    </div>
  );

  // Render detailed indicator
  const renderDetailedIndicator = () => (
    <Card
      className="network-indicator-card"
      size="small"
      style={style}
      bodyStyle={{ padding: "8px 12px" }}
    >
      <Space size="small" align="center">
        <WifiOutlined 
          style={{ color: quality.color, fontSize: "16px" }}
          aria-hidden="true"
        />
        <div>
          <Text strong style={{ color: quality.color }}>
            {quality.label}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {getFormattedSpeed()}
          </Text>
        </div>
        {showRefreshButton && (
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined spin={isRefreshing} />}
            onClick={handleRefresh}
            disabled={isRefreshing}
            title="Refresh network status"
          />
        )}
      </Space>
    </Card>
  );

  const indicator = showDetails ? renderDetailedIndicator() : renderSimpleIndicator();

  // Wrap with tooltip for simple indicator
  if (!showDetails) {
    return (
      <Tooltip
        title={renderTooltipContent()}
        placement="bottomLeft"
        trigger="hover"
        overlayClassName="network-indicator-tooltip-overlay"
      >
        <div 
          className={`network-indicator network-indicator--${position} ${className}`}
          data-testid="network-indicator"
        >
          {indicator}
        </div>
      </Tooltip>
    );
  }

  // Return detailed indicator without tooltip
  return (
    <div 
      className={`network-indicator network-indicator--${position} ${className}`}
      data-testid="network-indicator"
    >
      {indicator}
    </div>
  );
};

NetworkIndicator.propTypes = {
  /** Position of the indicator */
  position: PropTypes.oneOf([
    "top-left",
    "top-right", 
    "bottom-left",
    "bottom-right",
    "inline"
  ]),
  /** Show detailed view with speed/latency info */
  showDetails: PropTypes.bool,
  /** Show refresh button */
  showRefreshButton: PropTypes.bool,
  /** Additional CSS class */
  className: PropTypes.string,
  /** Size of the indicator */
  size: PropTypes.oneOf(["small", "medium", "large"]),
  /** Custom inline styles */
  style: PropTypes.object
};

export default React.memo(NetworkIndicator);
